import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { ITMS } from "@/constants/testIds";
import { useDepots, fmtDepot } from "@/lib/depots";
import { MapContainer, TileLayer, Marker, Tooltip as LTooltip, Popup } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import { busIcon, pulseBusIcon, depotIcon, TILE_URL, TILE_ATTR, DEPOTS, STATUS_COLOR as SC } from "@/lib/mapIcons";
import { BreakdownPopup, BreakdownAlertPanel, MobileBreakdownSheet } from "@/components/BreakdownWidgets";
import { toast } from "sonner";
import { Warning, BatteryHigh as Battery, Speedometer, X, Funnel, Lightning } from "@phosphor-icons/react";

const COLUMNS = [
  { id: "Available", label: "Available", color: "#22C55E" },
  { id: "On Trip", label: "On Trip", color: "#1E3A8A" },
  { id: "Breakdown", label: "Breakdown", color: "#EF4444" },
  { id: "Maintenance", label: "Maintenance", color: "#F97316" },
  { id: "Charging", label: "Charging", color: "#06B6D4" },
  { id: "Reserved", label: "Reserved", color: "#8B5CF6" },
];

export default function DispatchBoard() {
  const depotMap = useDepots();
  const [buses, setBuses] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [hoverCol, setHoverCol] = useState(null);
  const [sel, setSel] = useState(null);
  const [filters, setFilters] = useState({ depot: "All", type: "All", evOnly: false });
  const [mobileBreakdownBus, setMobileBreakdownBus] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const load = async () => {
    const [b, a] = await Promise.all([api.get("/fleet/buses"), api.get("/alerts")]);
    setBuses(b.data); setAlerts(a.data);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  const filteredBuses = useMemo(()=>buses.filter(b =>
    (filters.depot==="All" || b.depot===filters.depot) &&
    (filters.type==="All" || b.bus_type===filters.type) &&
    (!filters.evOnly || (b.fuel_type||"").includes("Electric") || b.bus_type?.includes("EV"))
  ), [buses, filters]);

  const byCol = useMemo(() => {
    const m = Object.fromEntries(COLUMNS.map((c) => [c.id, []]));
    for (const b of filteredBuses) (m[b.status] = m[b.status] || []).push(b);
    return m;
  }, [filteredBuses]);

  const depots = useMemo(()=>["All", ...new Set(buses.map(b=>b.depot))], [buses]);
  const types = useMemo(()=>["All", ...new Set(buses.map(b=>b.bus_type))], [buses]);

  const onDrop = async (newStatus) => {
    if (!dragId) return;
    const bus = buses.find((b) => b.id === dragId);
    if (!bus || bus.status === newStatus) { setDragId(null); setHoverCol(null); return; }
    setBuses((p) => p.map((b) => (b.id === dragId ? { ...b, status: newStatus } : b)));
    try {
      await api.patch(`/fleet/buses/${dragId}/status`, { status: newStatus });
      toast.success(`${bus.fleet_id} → ${newStatus}`);
    } catch {
      toast.error("Status update failed");
      load();
    } finally {
      setDragId(null); setHoverCol(null);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row h-full">
      {/* LEFT — Live Map */}
      <section className="lg:w-[420px] shrink-0 border-r border-slate-200 flex flex-col h-[50vh] lg:h-auto">
        <div className="px-4 lg:px-5 py-3 lg:py-4 border-b border-slate-200 bg-white">
          <div className="label-cap text-slate-500">Live Operations</div>
          <h1 className="font-display text-lg lg:text-xl font-bold">Dispatch · Goa Network</h1>
        </div>
        <div data-testid={ITMS.dispatchMap} className="flex-1 relative">
          <MapContainer center={[15.39, 73.99]} zoom={10} style={{ height: "100%", width: "100%" }}>
            <TileLayer url={TILE_URL} attribution={TILE_ATTR}/>
            {DEPOTS.map(d => {
              const fleet = buses.filter(b => b.depot === d.name);
              return (<Marker key={d.name} position={[d.lat, d.lng]} icon={depotIcon()}>
                <Popup><div className="text-xs"><div className="font-bold text-[#0F1A3D]">{d.name} Depot</div><div>Fleet: <b>{fleet.length}</b></div></div></Popup>
              </Marker>);
            })}
            <MarkerClusterGroup chunkedLoading disableClusteringAtZoom={12} maxClusterRadius={50}>
            {buses.filter(b=>b.status!=="Breakdown").map((b) => (
              <Marker key={b.id} position={[b.lat, b.lng]} icon={busIcon(b.status, 28)}>
                <Popup>
                  <div className="text-xs space-y-1 min-w-[170px]">
                    <div className="flex justify-between"><span className="font-mono font-bold text-[#1E3A8A]">{b.fleet_id}</span><span className="px-1.5 py-0.5 rounded text-white text-[10px]" style={{background:SC[b.status]}}>{b.status}</span></div>
                    <div className="font-semibold">{b.registration_number}</div>
                    <div className="text-slate-500">{b.category} · {fmtDepot(b.depot, depotMap)} · {b.speed} km/h</div>
                  </div>
                </Popup>
              </Marker>
            ))}
            </MarkerClusterGroup>
            {buses.filter(b=>b.status==="Breakdown").map((b) => (
              <Marker key={b.id} position={[b.lat, b.lng]} icon={pulseBusIcon(b.status, 38)}
                      eventHandlers={{ click: () => { setSel(b); if (isMobile) setMobileBreakdownBus(b); } }}
                      zIndexOffset={1000}>
                {!isMobile && <BreakdownPopup bus={b}/>}
              </Marker>
            ))}
          </MapContainer>
          <BreakdownAlertPanel buses={buses} onSelect={(b)=>{ setSel(b); if (isMobile) setMobileBreakdownBus(b); }}/>
        </div>
        <div className="border-t border-slate-200 bg-white p-3 max-h-40 overflow-auto scroll-thin">
          <div className="label-cap text-slate-500 mb-2 flex items-center gap-1.5">
            <Warning size={12} className="text-[#EF4444]" />Active Alerts
          </div>
          <div className="space-y-1.5">
            {alerts.filter(a => !a.resolved).slice(0, 5).map((a) => (
              <div key={a.id} className="flex items-center gap-2 text-xs">
                <span className={`w-1.5 h-1.5 rounded-full ${a.severity==="High"?"bg-[#EF4444]":a.severity==="Medium"?"bg-[#F97316]":"bg-slate-400"}`}></span>
                <span className="font-mono text-[#1E3A8A] font-semibold">{a.fleet_id}</span>
                <span className="text-slate-700">{a.type}</span>
                <span className="text-slate-400 ml-auto">{new Date(a.timestamp).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RIGHT — Kanban */}
      <section className="flex-1 flex flex-col min-w-0 bg-slate-50">
        <div className="px-4 lg:px-5 py-3 border-b border-slate-200 bg-white">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div>
              <div className="label-cap text-slate-500">Status Pipeline</div>
              <h2 className="font-display text-lg lg:text-xl font-bold">Kanban · <span className="hidden sm:inline">Drag to update status</span><span className="sm:hidden">Swipe ←→ to see columns</span></h2>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Funnel size={14} className="text-slate-500"/>
            <span className="label-cap text-slate-500 mr-1">Depot</span>
            {depots.map(d=>(
              <button key={d} onClick={()=>setFilters({...filters, depot:d})}
                className={`px-2.5 py-1 rounded-full transition-colors ${filters.depot===d?"bg-[#1E3A8A] text-white font-semibold":"bg-slate-100 hover:bg-slate-200 text-slate-700"}`}>{d === "All" ? "All" : fmtDepot(d, depotMap)}</button>
            ))}
            <span className="w-px h-4 bg-slate-200 mx-1"/>
            <span className="label-cap text-slate-500 mr-1">Type</span>
            {types.map(t=>(
              <button key={t} onClick={()=>setFilters({...filters, type:t})}
                className={`px-2.5 py-1 rounded-full transition-colors ${filters.type===t?"bg-[#1E3A8A] text-white font-semibold":"bg-slate-100 hover:bg-slate-200 text-slate-700"}`}>{t}</button>
            ))}
            <span className="w-px h-4 bg-slate-200 mx-1"/>
            <button onClick={()=>setFilters({...filters, evOnly: !filters.evOnly})}
              className={`px-2.5 py-1 rounded-full flex items-center gap-1 transition-colors ${filters.evOnly?"bg-[#22C55E] text-white font-semibold":"bg-slate-100 hover:bg-slate-200 text-slate-700"}`}>
              <Lightning size={11} weight={filters.evOnly?"fill":"regular"}/>EV Only
            </button>
            <span className="ml-auto text-slate-500">Showing <b>{filteredBuses.length}</b> of {buses.length}</span>
          </div>
        </div>
        <div data-testid={ITMS.dispatchKanban} className="flex-1 overflow-auto scroll-thin p-3 lg:p-4">
          <div className="flex gap-3 h-full" style={{ minWidth: "max-content" }}>
            {COLUMNS.map((c) => (
              <div key={c.id}
                   onDragOver={(e) => { e.preventDefault(); setHoverCol(c.id); }}
                   onDragLeave={() => setHoverCol(null)}
                   onDrop={() => onDrop(c.id)}
                   className={`w-72 max-w-[85vw] shrink-0 bg-white border-2 rounded-lg flex flex-col ${hoverCol === c.id ? "border-[#1E3A8A] bg-blue-50/30" : "border-slate-200"}`}>
                <div className="px-3 py-2.5 border-b-2 rounded-t-lg flex items-center justify-between" style={{ borderColor: c.color, background: c.color + "12" }}>
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full shadow" style={{ background: c.color }}></span>
                    <span className="text-sm font-bold" style={{ color: c.color }}>{c.label}</span>
                  </div>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full text-white" style={{ background: c.color }}>{(byCol[c.id] || []).length}</span>
                </div>
                <div className="flex-1 overflow-auto scroll-thin space-y-2 p-2">
                  {(byCol[c.id] || []).map((b) => (
                    <div key={b.id}
                         draggable
                         onClick={()=>setSel(b)}
                         onDragStart={() => setDragId(b.id)}
                         onDragEnd={() => setDragId(null)}
                         className="bg-white border-l-4 border border-slate-200 rounded-md p-3 cursor-pointer active:cursor-grabbing hover:shadow-md hover:border-l-[6px] transition-all"
                         style={{ borderLeftColor: c.color }}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-mono text-sm font-black text-[#1E3A8A]">{b.fleet_id}</div>
                        <span className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-700 rounded font-semibold">{fmtDepot(b.depot, depotMap)}</span>
                      </div>
                      <div className="text-sm font-semibold text-slate-800">{b.registration_number}</div>
                      <div className="text-[11px] text-slate-500 mb-2">{b.bus_type} · {b.manufacturer}</div>
                      <div className="grid grid-cols-3 gap-1.5 mb-2">
                        <div className="bg-slate-50 rounded px-1.5 py-1 text-center">
                          <div className="text-[9px] uppercase tracking-wide text-slate-400 font-semibold">SOC</div>
                          <div className="text-xs font-bold" style={{ color: b.soc < 30 ? "#EF4444" : b.soc < 60 ? "#F97316" : "#22C55E" }}>{b.soc}%</div>
                        </div>
                        <div className="bg-slate-50 rounded px-1.5 py-1 text-center">
                          <div className="text-[9px] uppercase tracking-wide text-slate-400 font-semibold">Speed</div>
                          <div className="text-xs font-bold text-slate-800">{b.speed}<span className="text-[9px] text-slate-400"> km/h</span></div>
                        </div>
                        <div className="bg-slate-50 rounded px-1.5 py-1 text-center">
                          <div className="text-[9px] uppercase tracking-wide text-slate-400 font-semibold">Delay</div>
                          <div className="text-xs font-bold" style={{ color: b.delay_min > 5 ? "#EF4444" : b.delay_min > 0 ? "#F97316" : "#22C55E" }}>{b.delay_min > 0 ? `+${b.delay_min}m` : "OK"}</div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-[10px] mb-1">
                        <span className="text-slate-500 font-medium">Occupancy</span>
                        <span className="font-bold tabular-nums">{b.occupancy_pct}%</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${b.occupancy_pct}%`, background: b.occupancy_pct > 80 ? "#EF4444" : b.occupancy_pct > 50 ? "#F97316" : "#22C55E" }}></div>
                      </div>
                    </div>
                  ))}
                  {(byCol[c.id] || []).length === 0 && (
                    <div className="text-center text-xs text-slate-400 py-8 italic">No buses · drop here</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {sel && (
        <div className="fixed top-14 right-0 bottom-0 w-[400px] bg-white border-l border-slate-200 z-40 shadow-2xl flex flex-col">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between" style={{background: (COLUMNS.find(c=>c.id===sel.status)?.color||"#1E3A8A")+"14"}}>
            <div>
              <div className="label-cap text-slate-500">Bus Detail</div>
              <h3 className="font-display text-xl font-black text-[#1E3A8A]">{sel.fleet_id}</h3>
              <div className="text-sm font-semibold text-slate-700">{sel.registration_number}</div>
            </div>
            <button onClick={()=>setSel(null)} className="text-slate-500 hover:text-slate-900"><X size={20}/></button>
          </div>
          <div className="flex-1 overflow-auto scroll-thin p-4 space-y-4 text-sm">
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-slate-50 rounded p-2 text-center"><div className="text-[9px] uppercase text-slate-400 font-semibold">SOC</div><div className="font-bold text-lg" style={{color:sel.soc<30?"#EF4444":sel.soc<60?"#F97316":"#22C55E"}}>{sel.soc}%</div></div>
              <div className="bg-slate-50 rounded p-2 text-center"><div className="text-[9px] uppercase text-slate-400 font-semibold">Speed</div><div className="font-bold text-lg">{sel.speed}<span className="text-xs"> km/h</span></div></div>
              <div className="bg-slate-50 rounded p-2 text-center"><div className="text-[9px] uppercase text-slate-400 font-semibold">Delay</div><div className="font-bold text-lg" style={{color:sel.delay_min>5?"#EF4444":sel.delay_min>0?"#F97316":"#22C55E"}}>{sel.delay_min>0?`+${sel.delay_min}m`:"OK"}</div></div>
            </div>

            <div>
              <div className="label-cap text-slate-500 mb-2">Vehicle</div>
              <div className="space-y-1.5 bg-slate-50 rounded-md p-3">
                {[["Status", sel.status],["Type", sel.bus_type],["Category", sel.category],["Manufacturer", sel.manufacturer],["Depot", fmtDepot(sel.depot, depotMap)],["Ownership", sel.is_private?"Private Operator":"KTCL Owned"]].map(([k,v])=>(
                  <div key={k} className="flex justify-between text-xs"><span className="text-slate-500">{k}</span><span className="font-semibold">{v}</span></div>
                ))}
              </div>
            </div>

            <div>
              <div className="label-cap text-slate-500 mb-2">Telematics & Health</div>
              <div className="space-y-1.5 bg-slate-50 rounded-md p-3">
                {[["GPS Device", sel.gps_device_id],["ETM Device", sel.etm_device_id],["RFID Reader", sel.rfid_reader_id],["Odometer", `${(sel.odometer ?? 0).toLocaleString("en-IN")} km`],["Battery Health", `${sel.battery_health ?? 0}%`],["Occupancy", `${sel.occupancy_pct ?? 0}%`]].map(([k,v])=>(
                  <div key={k} className="flex justify-between text-xs"><span className="text-slate-500">{k}</span><span className="font-mono font-semibold">{v}</span></div>
                ))}
              </div>
            </div>

            <div>
              <div className="label-cap text-slate-500 mb-2">Compliance</div>
              <div className="space-y-1.5 bg-slate-50 rounded-md p-3">
                {[["Chassis", sel.chassis_number],["Engine", sel.engine_number],["Insurance Due", sel.insurance_due],["Permit Due", sel.permit_due],["Service Due", sel.service_due]].map(([k,v])=>(
                  <div key={k} className="flex justify-between text-xs"><span className="text-slate-500">{k}</span><span className="font-mono font-semibold">{v}</span></div>
                ))}
              </div>
            </div>

            <div>
              <div className="label-cap text-slate-500 mb-2">Recent Alerts</div>
              <div className="space-y-1.5">
                {alerts.filter(a=>a.fleet_id===sel.fleet_id).slice(0,5).map(a=>(
                  <div key={a.id} className="flex items-start gap-2 text-xs bg-slate-50 rounded p-2">
                    <span className={`w-1.5 h-1.5 rounded-full mt-1 ${a.severity==="High"?"bg-[#EF4444]":a.severity==="Medium"?"bg-[#F97316]":"bg-slate-400"}`}/>
                    <div className="flex-1"><div className="font-semibold">{a.type}</div><div className="text-slate-500 text-[11px]">{new Date(a.timestamp).toLocaleString()}</div></div>
                  </div>
                ))}
                {alerts.filter(a=>a.fleet_id===sel.fleet_id).length===0 && <div className="text-xs text-slate-400 italic text-center py-2">No alerts for this bus</div>}
              </div>
            </div>
          </div>
          <div className="p-4 border-t border-slate-200 grid grid-cols-2 gap-2">
            <button onClick={async ()=>{ await api.patch(`/fleet/buses/${sel.id}/status`, {status:"Maintenance"}); toast.success("Sent to Maintenance"); load(); setSel(null);}} className="px-3 py-2 border border-slate-200 rounded text-xs font-semibold hover:bg-slate-100">Send to Maintenance</button>
            <button onClick={async ()=>{ await api.patch(`/fleet/buses/${sel.id}/status`, {status:"Available"}); toast.success("Marked Available"); load(); setSel(null);}} className="ktcl-btn-primary rounded text-xs font-semibold">Mark Available</button>
          </div>
        </div>
      )}

      <MobileBreakdownSheet bus={mobileBreakdownBus} onClose={()=>setMobileBreakdownBus(null)}/>
    </div>
  );
}
